import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { PageTitles, pageTitleMap } from '../models/enums';

@Injectable({
  providedIn: 'root',
})
export class CustomTitleStrategy extends TitleStrategy {
  constructor(private readonly title: Title) {
    super();
  }

  public override updateTitle(routerState: RouterStateSnapshot): void {
    const titleInfo = this.buildTitleInfo(routerState);

    // Update the browser title
    if (titleInfo.fullTitle !== undefined) {
      this.title.setTitle(titleInfo.fullTitle);
    }
  }

  private buildTitleInfo(snapshot: RouterStateSnapshot): {
    pageTitle: PageTitles | null;
    suffix: string | null;
    fullTitle: string;
  } {
    // Get the URL segments
    const url = snapshot.url;
    const urlSegments = this.getUrlBase(url);

    // Check for query parameters that might affect the title
    let titleSuffix: string | null = null;
    const queryParams = this.getQueryParams(snapshot);

    if (
      ['starred', 'vault', 'files'].some((segment) => url.includes(`/${segment}`)) &&
      queryParams['showDeleted'] === '1'
    ) {
      titleSuffix = '(Deleted)';
    }

    // Map URL segments to page titles
    let pageTitle: PageTitles | null = null;

    switch (urlSegments) {
      case 'files':
        pageTitle = PageTitles.FILES;
        break;
      case 'vault':
        pageTitle = PageTitles.VAULT;
        break;
      case 'starred':
        pageTitle = PageTitles.STARRED;
        break;
      case 'recents':
        pageTitle = PageTitles.RECENTS;
        break;
      case 'shares':
        pageTitle = PageTitles.SHARES;
        break;
      case 'settings':
        pageTitle = PageTitles.SETTINGS;
        break;
      case 'users':
        pageTitle = url.includes('/users/roles') ? PageTitles.ROLES : PageTitles.USERS;
        break;
      case 'dashboard':
        pageTitle = PageTitles.DASHBOARD;
        break;
      default:
        // Try to get from pageTitleMap
        pageTitle = pageTitleMap[urlSegments] || null;
        break;
    }

    const pageTitleText = pageTitle || 'Sync';
    const fullTitle = titleSuffix ? `${pageTitleText} ${titleSuffix}` : pageTitleText;

    return {
      pageTitle,
      suffix: titleSuffix,
      fullTitle,
    };
  }

  private getUrlBase(url: string): string {
    const urlSegments = url.split('/')[1];
    if (urlSegments && urlSegments.includes('?')) {
      return urlSegments.split('?')[0];
    }
    return urlSegments || '';
  }

  private getQueryParams(snapshot: RouterStateSnapshot): { [key: string]: string } {
    let current = snapshot.root;
    const params: { [key: string]: string } = {};

    while (current) {
      Object.assign(params, current.queryParams);
      current = current.firstChild;
    }

    return params;
  }
}
